import { useCallback, useEffect, useRef } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { SubscriptionState } from "centrifuge";
import {
  acquireSubscription,
  type SubscriptionHandle,
} from "@/features/chat/api/realtime";
import { aronKeys, invalidateAronConversations, invalidateAronUsage } from "../api/aronApi";
import type {
  AronMessage,
  AronPendingWrite,
  AronStreamEvent,
  AronToolStep,
} from "../types";

function upsertStep(steps: AronToolStep[], step: AronToolStep): AronToolStep[] {
  const i = steps.findIndex((s) => s.id === step.id);
  if (i === -1) return [...steps, step];
  const next = steps.slice();
  next[i] = { ...next[i], ...step };
  return next;
}

function upsertWrite(
  writes: AronPendingWrite[],
  write: AronPendingWrite,
): AronPendingWrite[] {
  const i = writes.findIndex((w) => w.id === write.id);
  if (i === -1) return [...writes, write];
  const next = writes.slice();
  next[i] = { ...next[i], ...write };
  return next;
}

function patch(old: AronMessage[] | undefined, evt: AronStreamEvent): AronMessage[] {
  const list = old ?? [];
  const ensure = (id: string): AronMessage[] =>
    list.some((m) => m.id === id)
      ? list
      : [
          ...list,
          {
            id,
            role: "assistant",
            text: "",
            status: "streaming",
            created_at: new Date().toISOString(),
            tool_steps: [],
            sources: [],
          } as AronMessage,
        ];

  const map = (id: string, fn: (m: AronMessage) => AronMessage) =>
    ensure(id).map((m) => (m.id === id ? fn(m) : m));

  switch (evt.type) {
    case "message.start":
      return ensure(evt.message_id);
    case "delta":
      return map(evt.message_id, (m) => ({ ...m, text: m.text + evt.text, status: "streaming" }));
    case "thinking":
      return map(evt.message_id, (m) => ({
        ...m,
        thinking: (m.thinking ?? "") + evt.text,
        status: "streaming",
      }));
    case "quality_gate":
      // Zapowiedź ("kontroler sprawdza…") i wynik lecą tym samym kanałem; oba trafiają
      // na wiadomość, żeby widok mógł pokazać pasek pracy, a potem ocenę.
      return map(evt.message_id, (m) => ({ ...m, gate: { ...(m.gate ?? {}), ...evt.gate } }));
    case "tool_step":
      return map(evt.message_id, (m) => ({ ...m, tool_steps: upsertStep(m.tool_steps, evt.step) }));
    case "file":
      return map(evt.message_id, (m) => {
        const files = m.files ?? [];
        return files.some((f) => f.id === evt.file.id)
          ? m
          : { ...m, files: [...files, evt.file] };
      });
    case "write_proposal":
    case "write_resolved":
      return map(evt.message_id, (m) => ({
        ...m,
        pending_writes: upsertWrite(m.pending_writes ?? [], evt.write),
      }));
    case "message.end":
      return map(evt.message_id, (m) => ({
        ...m,
        text: evt.message.content,
        status: evt.message.status,
        thinking: evt.message.thinking ?? m.thinking,
        sources: evt.message.sources ?? m.sources,
        model_id: evt.message.model_id ?? m.model_id,
        cost_usd: evt.message.cost_usd,
      }));
    case "usage":
      // Rachunek na bieżąco: dymek pokazuje koszt w trakcie tury, message.end
      // podmienia go potem na ostateczny (ten sam licznik po stronie serwera).
      return map(evt.message_id, (m) => ({
        ...m,
        cost_usd: evt.cost_usd,
        provider_calls: evt.provider_calls,
      }));
    case "message_appended":
      return list.some((m) => m.id === evt.message.id)
        ? list
        : [
            ...list,
            {
              id: evt.message.id,
              role: "assistant",
              text: evt.message.text,
              status: "done",
              created_at: evt.message.created_at,
              tool_steps: [],
              sources: [],
            } as AronMessage,
          ];
    case "user_addendum":
      // Dopisek z innej karty albo z docka. Nadawca ma go już z wysyłki optymistycznej
      // (podmienionej na wiersz z serwera w onSuccess), więc bez dublowania.
      return list.some((m) => m.id === evt.message.id) ? list : [...list, evt.message];
    case "addendum_seen":
      return list.map((m) =>
        m.id === evt.addendum_id
          ? { ...m, addendum_seen_at: m.addendum_seen_at ?? new Date().toISOString() }
          : m,
      );
    case "error":
      return map(evt.message_id, (m) => ({ ...m, status: "error", error: evt.error }));
    case "cancelled":
      return map(evt.message_id, (m) => ({ ...m, status: "cancelled" }));
    case "turn_rolled_back":
      return map(evt.message_id, (m) => ({
        ...m,
        status: "error",
        error: `Tura wycofana — baza wróciła do stanu sprzed tury. Powód: ${evt.reason}`,
      }));
    default:
      return list;
  }
}

// ── Per-channel shared subscription ──────────────────────────────────────────
// A single ARON conversation can be watched by more than one view at once (the
// full page AND the detached dock). The publication MUST mutate the message
// cache exactly once, so the cache-patching handler is installed PER CHANNEL and
// ref-counted across consumers — never per consumer (two handlers would apply
// every streamed delta twice). Each consumer only registers its own terminal
// listener, fanned out by the shared handler.
type TerminalListener = (messageId: string) => void;

interface ChannelSub {
  refCount: number;
  handle: SubscriptionHandle | null;
  handler: (ctx: { data: unknown }) => void;
  terminalListeners: Set<TerminalListener>;
  ready: Promise<void>;
}

const aronChannels = new Map<string, ChannelSub>();

function acquireAronChannel(id: string, qc: QueryClient): ChannelSub {
  let entry = aronChannels.get(id);
  if (!entry) {
    const terminalListeners = new Set<TerminalListener>();
    const handler = (ctx: { data: unknown }) => {
      const evt = ctx.data as AronStreamEvent;
      qc.setQueryData<AronMessage[]>(aronKeys.messages(id), (old) => patch(old, evt));
      // Refresh the conversation list when a turn starts/ends so its is_generating
      // spinner appears and clears (covers turns started from any tab/the dock).
      if (evt.type === "message.start" || evt.type === "message.end") {
        invalidateAronConversations(qc);
      }
      if (evt.type === "message.end") {
        invalidateAronUsage(qc);
      }
      // Wycofanie tury jest jej KOŃCEM — bez tego „wysyłanie" nie gasło do F5.
      if (evt.type === "turn_rolled_back") {
        invalidateAronConversations(qc);
        invalidateAronUsage(qc);
      }
      if (
        evt.type === "message.end" ||
        evt.type === "error" ||
        evt.type === "cancelled" ||
        evt.type === "turn_rolled_back"
      ) {
        terminalListeners.forEach((fn) => fn(evt.message_id));
      }
    };

    const created: ChannelSub = {
      refCount: 0,
      handle: null,
      handler,
      terminalListeners,
      ready: Promise.resolve(),
    };
    created.ready = (async () => {
      const handle = await acquireSubscription(`aron:${id}`);
      handle.sub.on("publication", handler);
      if (handle.sub.state !== SubscriptionState.Subscribed) {
        await new Promise<void>((resolve) => {
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            handle.sub.off("subscribed", finish);
            resolve();
          };
          handle.sub.on("subscribed", finish);
          setTimeout(finish, 2000); // safety: never hang the send
        });
      }
      if (aronChannels.get(id) === created) {
        created.handle = handle;
      } else {
        // everyone released while we were subscribing — drop it
        try {
          handle.sub.off("publication", handler);
          handle.release();
        } catch {
          // ignore
        }
      }
    })();

    entry = created;
    aronChannels.set(id, entry);
  }
  entry.refCount += 1;
  return entry;
}

function releaseAronChannel(id: string, listener: TerminalListener): void {
  const entry = aronChannels.get(id);
  if (!entry) return;
  entry.terminalListeners.delete(listener);
  entry.refCount -= 1;
  if (entry.refCount <= 0) {
    aronChannels.delete(id);
    // Wait for any in-flight subscribe to settle before tearing down so we don't
    // race the `handle` assignment inside the ready promise.
    void entry.ready.finally(() => {
      try {
        entry.handle?.sub.off("publication", entry.handler);
        entry.handle?.release();
      } catch {
        // ignore
      }
    });
  }
}

/**
 * Live ARON stream manager. Hosted in the stable (non-remounting) parent
 * (AronPage / AronDock) so the subscription survives the conversation-create
 * flow.
 *
 * Returns `ensureSubscribed(id)` which resolves only once the Centrifugo channel
 * is actually in the "subscribed" state — the send flow awaits it BEFORE
 * dispatching the turn, so the worker never streams deltas before we're
 * listening. The underlying channel subscription is shared + ref-counted, so the
 * same conversation open in several views streams correctly (and only once).
 */
export function useAronLive(
  conversationId: string | null,
  onTerminal?: TerminalListener,
) {
  const qc = useQueryClient();
  const onTerminalRef = useRef(onTerminal);
  onTerminalRef.current = onTerminal;

  // One stable listener identity per hook instance (for Set add/delete).
  const listenerRef = useRef<TerminalListener>();
  if (!listenerRef.current) {
    listenerRef.current = (messageId) => onTerminalRef.current?.(messageId);
  }
  // The channel id this instance currently holds (we watch one at a time).
  const heldRef = useRef<string | null>(null);

  const ensureSubscribed = useCallback(
    async (id: string): Promise<void> => {
      const listener = listenerRef.current!;
      if (heldRef.current === id) {
        await aronChannels.get(id)?.ready;
        return;
      }
      if (heldRef.current) releaseAronChannel(heldRef.current, listener);
      const entry = acquireAronChannel(id, qc);
      entry.terminalListeners.add(listener);
      heldRef.current = id;
      await entry.ready;
    },
    [qc],
  );

  // Subscribe when an existing conversation is opened (selected from the list).
  useEffect(() => {
    if (conversationId) void ensureSubscribed(conversationId);
  }, [conversationId, ensureSubscribed]);

  // Release on unmount (NOT on every id change) so the create→send flow isn't
  // torn down mid-flight; ensureSubscribed handles switching channels.
  useEffect(
    () => () => {
      if (heldRef.current) {
        releaseAronChannel(heldRef.current, listenerRef.current!);
        heldRef.current = null;
      }
    },
    [],
  );

  return { ensureSubscribed };
}
